/* eslint-disable @typescript-eslint/no-explicit-any */
//hiring/job/get-recently-added-candidates/
/**
 * export const getProjects = (): Promise<{
  results: ProjectDetails[];
  count: number;
  page: number;
  total_pages: number;
}> => httpApi.get<any>("hiring/job/").then(({ data }) => data);
 */

import {
  CustomTag,
  DeleteCustomTagPayload,
  IRecentlyAddedCandidateResponse,
} from "interfaces/ICandidate";
import { httpApi } from "./http.api";
import { updatedCandidates, updatedSingleCandidates } from "utils/utils";

const inFlightShortlistRequests = new Map<string, Promise<IRecentlyAddedCandidateResponse>>();

export const getRecentlyAddedCandidates = (
  page: number
): Promise<IRecentlyAddedCandidateResponse> =>
  httpApi.get(`hiring/job/get-recently-added-candidates/?page=${page}`).then(({ data }) => data);

export const getAllCandidates = (
  projectId: number,
  page: number,
  keyword?: string
): Promise<IRecentlyAddedCandidateResponse> => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  if (keyword?.trim()) params.append("keyword", keyword.trim());
  return httpApi
    .get(`hiring/suggested-candidates-for-job/${projectId}/?${params.toString()}`)
    .then(({ data }) => data);
};

export const getImportedCandidates = (
  projectId: number,
  page: number
): Promise<IRecentlyAddedCandidateResponse> =>
  httpApi.get(`hiring/imported-candidates-for-job/${projectId}/?page=${page}`).then(({ data }) => {
    const candidates = updatedCandidates(data?.results);
    const structuredData = {
      ...data,
      results: candidates,
    };
    return structuredData;
  });

export const getRejectedCandidates = (
  projectId: number,
  page: number
): Promise<IRecentlyAddedCandidateResponse> =>
  httpApi.get(`hiring/rejected-candidates-for-job/${projectId}/?page=${page}`).then(({ data }) => {
    const candidates = updatedCandidates(data?.results);
    const structuredData = {
      ...data,
      results: candidates,
    };
    return structuredData;
  });

export const getShortlistedCandidates = (
  projectId: number,
  page: number,
  name?: string,
  tag?: string[],
  interview_status?: string[],
  date_added?: string,
  // custom_tags?: any,
  select_all?: boolean,
  sort_by?: string,
  sort_order?: string,
  search_log_id?: string,
  db?: any,
  profile_origin?: string[],
  keyword?: string
): Promise<IRecentlyAddedCandidateResponse> => {
  const params = new URLSearchParams();

  params.append("page", page.toString());
  params.append("get_interview_details", "True");

  if (name) params.append("name", name);
  if (keyword?.trim()) params.append("keyword", keyword.trim());
  if (date_added) params.append("date_added", date_added);
  if (select_all) params.append("select_all", "true");

  if (tag?.length) params.append("tags", tag.join("|"));
  if (interview_status?.length) params.append("interview_status", interview_status.join("|"));
  if (db?.length) params.append("db", db.join("|"));
  if (profile_origin?.length) params.append("profile_origin", profile_origin.join("|"));
  if (sort_by) params.append("sort_by", sort_by);
  if (sort_order) params.append("sort_order", sort_order);
  if (search_log_id) params.append("search_log_id", search_log_id);
  // if (custom_tags?.length) params.append("custom_tags", custom_tags.join("|"));
  const url = `hiring/shortlisted-candidates-for-job/${projectId}/?${params.toString()}`;

  const cached = inFlightShortlistRequests.get(url);
  if (cached) return cached;

  const req = httpApi
    .get(url)
    .then(({ data }) => {
      const candidates = updatedCandidates(data?.results);
      return {
        ...data,
        results: candidates,
      };
    })
    .finally(() => {
      inFlightShortlistRequests.delete(url);
    });

  inFlightShortlistRequests.set(url, req);
  return req;
};

export const getCandidate = (id: number): Promise<any> =>
  httpApi.get(`candidate/candidate/${id}/`).then(({ data }) => {
    const candidates = updatedSingleCandidates(data?.notes, data?.full_profile);
    const structuredData = {
      ...data,
      full_profile: candidates,
    };
    return structuredData;
  });

export const sortCandidates = (
  type: string,
  payload: Record<string, any> = {} // data to send in the request body
): Promise<IRecentlyAddedCandidateResponse> =>
  httpApi.post(`hiring/reorder-candidates/`, payload).then(({ data }) => data);

export const getCandidateSummery = (id: number): Promise<any> =>
  httpApi.get(`candidate/profile-summary/${id}/`).then(({ data }) => data);

export const bulkImportUrls = (rolePayload: any): Promise<any> =>
  httpApi
    .post<any>("hiring/custom-assignment/bulk-import-candidates-to-job/", rolePayload)
    .then(({ data }) => data);

export const exportCandidates = (payload: any): Promise<any> =>
  httpApi
    .post<any>("hiring/export-candidates-for-job/export-candidates/", payload)
    .then(({ data }) => data);

export const getCandidateAISummary = (canidateID: number): Promise<any> =>
  httpApi.get(`candidate/profile-summary/${canidateID}/`).then(({ data }) => data);

export const addCandidateNote = (candidateNotePayload: {
  custom_fields: {
    note: string;
    edited_by: string | null;
    modified_date: any;
    is_edited: boolean;
  };
  candidate: number;
}): Promise<any> =>
  httpApi.post<any>("candidate/notes/", { ...candidateNotePayload }).then(({ data }) => data);

export const editCandidate = (candidateNotePayload: {
  custom_fields: any;
  candidate: number;
}): Promise<any> =>
  httpApi.post<any>("candidate/notes/", { ...candidateNotePayload }).then(({ data }) => data);

export const updateCandidateNote = (
  noteId: any,
  candidateNotePayload: { note?: string; candidate: number; custom_fields?: any }
): Promise<any> =>
  httpApi
    .patch<any>("candidate/notes/" + noteId + "/", { ...candidateNotePayload })
    .then(({ data }) => data);

export const deleteCandidateNote = async (id: number, candidate: number): Promise<any> => {
  return httpApi.delete<any>(`candidate/notes/${id}/`, {
    data: { candidate: candidate },
  });
};

export const getCandidateDetails = (id: number): Promise<any[]> =>
  httpApi.get<any[]>("candidate/candidate/" + id + "/").then((data: any) => data.data);

export const getCandidateNoteList = (candidateId: number): Promise<any[]> =>
  httpApi.get<any[]>("candidate/notes/" + candidateId + "/").then((data: any) => data.data);

export const getCandidateLogs = (candidateId: number | string): Promise<any[]> =>
  httpApi
    .get<any[]>(`users/mail/get-email-log/?candidate_id=${candidateId}`)
    .then((data: any) => data.data);

// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
export const getCandidateNoteDetails = (noteId: number, candidateId: number): Promise<any[]> =>
  httpApi.get<any[]>("candidate/notes/" + noteId + "/").then((data: any) => data.data);

export const updateMediaLinks = (mediaLinkPayload: any): Promise<any> =>
  httpApi
    .post<any>("candidate/profile-assignment/add-link-to-profile/", {
      ...mediaLinkPayload,
    })
    .then(({ data }) => data);

export const markAsReviewed = (candidateId: number, jobId: number): Promise<any> =>
  httpApi
    .post<any>("candidate/custom-candidates/mark-as-reviewed/", {
      candidate_id: candidateId,
      job_id: jobId,
    })
    .then(({ data }) => data);

export const authorizationSuccess = ({
  state,
  hd,
  authuser,
  scope,
  code,
  prompt,
}: any): Promise<any> =>
  httpApi
    .get<any>(
      `users/gmail/confirm-authorization/?state=${state}&hd=${hd}&authuser=${authuser}&scope=${scope}&prompt=${prompt}&code=${code}`
    )
    .then(({ data }) => data);

export const sendEmail = (emailPayload: any): Promise<any> =>
  httpApi
    .post<any>("users/mail/send-mail/", emailPayload, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then(({ data }) => data);

export const getAISuggestedCandidatesForJob = (projectID: number): Promise<any> =>
  httpApi.get(`hiring/ai-suggested-candidates-for-job/${projectID}/`).then(({ data }) => data);

export const getAdvancedProfileSummery = (profileId: number): Promise<any> =>
  httpApi.get(`candidate/profile-search/${profileId}`).then(({ data }) => data);

export const referFriend = (payload: any): Promise<any> =>
  httpApi.post("users/user-custom/referral/", payload).then(({ data }) => data);

// Custom tags
export const addCustomTag = (tagPayload: {
  candidate: number;
  job: number;
  tag: string;
}): Promise<CustomTag> =>
  httpApi.post<CustomTag>("candidate/custom-tags/", tagPayload).then(({ data }) => data);

export const getCustomTagsByJob = (jobId: number): Promise<CustomTag[]> =>
  httpApi.get<CustomTag[]>(`candidate/custom-tags/?job_id=${jobId}`).then(({ data }) => data);

export const deleteCustomTag = (payload: DeleteCustomTagPayload): Promise<void> =>
  httpApi
    .delete("candidate/custom-tags/delete-custom-tag/", {
      data: payload,
    })
    .then(({ data }) => data);

export const uploadCandidateDocument = (candidateNotePayload: any): Promise<any> => {
  const formData = new FormData();
  formData.append("candidate", candidateNotePayload.candidate.toString());
  formData.append("document", candidateNotePayload.document);
  if (candidateNotePayload.custom_fields)
    formData.append("custom_fields", JSON.stringify(candidateNotePayload.custom_fields));

  return httpApi
    .post<any>("candidate/notes/", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then(({ data }) => data);
};

export const changeStage = (payload: {
  candidate_id: number;
  stage_name: string;
  project_id: number;
}): Promise<any> => httpApi.post<any>("ats/change-stage/", payload).then(({ data }) => data);
